/**
 * Home health demo store — minimal external store.
 *
 * Deliberately not a React context: presenter actions are async mutation chains
 * that read and write state outside React, and several product surfaces read the
 * store without any provider-tree insertion. React reads it via
 * `useSyncExternalStore` (see useDemoStore.ts).
 */
import type { DemoEvent } from "./events";
import { reduce } from "./reducer";
import type { DemoState } from "@caredna-marlin/utils/homeHealthDemo/state";

export type Listener = () => void;

export interface DemoStore {
  getState(): DemoState;
  dispatch(event: DemoEvent): void;
  subscribe(listener: Listener): () => void;
}

export function createDemoStore(
  initial: DemoState,
  reducer: (state: DemoState, event: DemoEvent) => DemoState = reduce,
): DemoStore {
  let state = initial;
  const listeners = new Set<Listener>();
  return {
    getState: () => state,
    dispatch(event) {
      const next = reducer(state, event);
      if (next === state) return;
      state = next;
      listeners.forEach((listener) => listener());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
